import { dcmjsGetString, dcmjsSetString } from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { DicomVRMaxLength } from './dicomConstants';

export abstract class DicomRegistration extends DicomBase {
    public readonly sourceFrameOfReferenceUid: string;
    public readonly targetFrameOfReferenceUid: string;

    constructor(
        sopUid: string,
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        contentLabel: string,
        dataset: DcmJsDatasetWrapper
    ) {
        super(sopUid, 'REG', dataset);
        this.sourceFrameOfReferenceUid = sourceFrameOfReferenceUid;
        this.targetFrameOfReferenceUid = targetFrameOfReferenceUid;
        this.contentLabel = contentLabel;
    }

    /**
     * Content Label Attribute Tag (0070,0080), VR: CS (Code String).
     * Max 16 characters. Values exceeding this are truncated.
     */
    public get contentLabel(): string {
        return dcmjsGetString(this.datasetWrapper, 'ContentLabel');
    }

    public set contentLabel(label: string) {
        const truncated =
            label.length > DicomVRMaxLength.SHORT
                ? label.slice(0, DicomVRMaxLength.SHORT)
                : label;
        dcmjsSetString(this.datasetWrapper, 'ContentLabel', truncated);
    }

    /**
     * Content Description Attribute Tag (0070,0081), VR: LO (Long String).
     * Max 64 characters.
     */
    public get contentDescription(): string {
        return dcmjsGetString(this.datasetWrapper, 'ContentDescription');
    }

    public set contentDescription(description: string) {
        const truncated =
            description.length > DicomVRMaxLength.LONG
                ? description.slice(0, DicomVRMaxLength.LONG)
                : description;
        dcmjsSetString(this.datasetWrapper, 'ContentDescription', truncated);
    }

    /**
     * Content Creator's Name Attribute Tag (0070,0084), VR: PN.
     * Handles DICOM Person Name format which dcmjs returns
     * as arrays with an Alphabetic property.
     */
    public get contentCreatorName(): string {
        const value = dcmjsGetString(this.datasetWrapper, 'ContentCreatorName');
        if (Array.isArray(value) && value.length > 0 && value[0]?.Alphabetic) {
            return value[0].Alphabetic as string;
        }
        return value;
    }

    /**
     * Content Date Attribute Tag (0008,0023), VR: DA.
     * Format: YYYYMMDD.
     */
    public get contentDate(): string {
        return dcmjsGetString(this.datasetWrapper, 'ContentDate');
    }

    /**
     * Content Time Attribute Tag (0008,0033), VR: TM.
     */
    public get contentTime(): string {
        return dcmjsGetString(this.datasetWrapper, 'ContentTime');
    }
}
